import styled from '@emotion/styled';
import { useRef, useState } from 'react';

import { Difficulty } from '~/utils/words';

export interface ShareStats {
  wpm: number;
  rawWpm: number;
  accuracy: number;
  correct: number;
  incorrect: number;
  timeMode: number;
  difficulty: Difficulty;
  rating: string;
  ratingColor: string;
}

/* ─── STYLED ─────────────────────────────────────────────────────────────── */
const Overlay = styled.div`
  align-items: center;
  background: rgba(10, 13, 18, 0.85);
  backdrop-filter: blur(6px);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: var(--space-lg);
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
  animation: fadeSlideUp var(--transition-slow) ease both;
`;

const ModalBox = styled.div`
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-lg);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6), var(--shadow-glow-accent);
  max-width: 520px;
  overflow: hidden;
  width: 100%;
`;

const ModalHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  padding: var(--space-md) var(--space-lg);
`;

const ModalTitle = styled.span`
  color: var(--color-text-secondary);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-label);
  text-transform: uppercase;
`;

const CloseBtn = styled.button`
  color: var(--color-text-muted);
  font-size: 1.1rem;
  line-height: 1;
  padding: 4px 8px;
  transition: color var(--transition-base);

  &:hover {
    color: var(--color-text-primary);
  }
`;

/* The branded share card — this div gets captured for the PNG */
const ShareCard = styled.div`
  background: var(--color-bg-surface);
  border-bottom: 1px solid var(--color-border);
  padding: var(--space-lg) var(--space-xl);
  position: relative;

  &::before {
    background: linear-gradient(90deg, var(--color-accent), transparent);
    content: '';
    height: 2px;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

const CardBrand = styled.div`
  color: var(--color-text-muted);
  font-family: var(--font-display);
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  margin-bottom: var(--space-lg);
  text-transform: uppercase;
`;

const CardRating = styled.div<{ $color: string }>`
  color: ${p => p.$color};
  font-family: var(--font-ui);
  font-size: 1.5rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.04em;
  margin-bottom: var(--space-md);
  text-transform: uppercase;
`;

const CardStatsRow = styled.div`
  display: flex;
  gap: var(--space-xl);
  margin-bottom: var(--space-sm);
`;

const CardStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const CardStatValue = styled.span<{ $color: string }>`
  color: ${p => p.$color};
  font-family: var(--font-display);
  font-size: 2.25rem;
  font-weight: var(--font-weight-bold);
  line-height: 1;
`;

const CardStatLabel = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: 0.65rem;
  letter-spacing: var(--letter-spacing-label);
  text-transform: uppercase;
`;

const CardMeta = styled.div`
  color: var(--color-text-muted);
  font-family: var(--font-display);
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  margin-top: var(--space-md);
`;

const CardRawLine = styled.div`
  color: var(--color-text-muted);
  font-family: var(--font-display);
  font-size: 0.8rem;
  margin-top: 2px;
`;

/* Bottom actions */
const Actions = styled.div`
  display: flex;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-lg);
`;

const ActionBtn = styled.button<{ $primary?: boolean }>`
  align-items: center;
  background: ${p => (p.$primary ? 'var(--color-accent-glow)' : 'transparent')};
  border: 1px solid ${p => (p.$primary ? 'var(--color-accent)' : 'var(--color-border-strong)')};
  border-radius: var(--radius-md);
  color: ${p => (p.$primary ? 'var(--color-accent)' : 'var(--color-text-secondary)')};
  cursor: pointer;
  display: flex;
  flex: 1;
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  gap: 6px;
  justify-content: center;
  letter-spacing: var(--letter-spacing-label);
  padding: 10px 16px;
  text-transform: uppercase;
  transition: all var(--transition-base);

  &:hover {
    background: var(--color-bg-subtle);
    border-color: var(--color-accent);
    color: var(--color-text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Feedback = styled.span`
  align-self: center;
  color: var(--color-correct);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  letter-spacing: var(--letter-spacing-label);
  text-transform: uppercase;
`;

/* ─── CANVAS PNG DOWNLOAD ─────────────────────────────────────────────────── */
function downloadPng(stats: ShareStats) {
  const W = 600;
  const H = 300;
  const canvas = document.createElement('canvas');
  canvas.width = W * 2; // retina
  canvas.height = H * 2;
  canvas.style.width = `${W}px`;
  canvas.style.height = `${H}px`;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(2, 2);

  // Background
  ctx.fillStyle = '#0f1318';
  ctx.fillRect(0, 0, W, H);

  // Top accent line
  const grad = ctx.createLinearGradient(0, 0, W, 0);
  grad.addColorStop(0, '#38bdf8');
  grad.addColorStop(1, 'transparent');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, 2);

  // Border
  ctx.strokeStyle = '#1e2a38';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, W - 1, H - 1);

  // Brand
  ctx.fillStyle = '#3d5269';
  ctx.font = '700 11px monospace';
  ctx.fillText('CRYOTYPE  ·  PRECISION TYPING TEST', 40, 40);

  // Rating
  const ratingColorMap: Record<string, string> = {
    'var(--color-accent)': '#38bdf8',
    'var(--color-wpm)': '#818cf8',
    'var(--color-correct)': '#34d399',
    'var(--color-accuracy)': '#34d399',
    'var(--color-time)': '#f472b6',
    'var(--color-text-secondary)': '#7a90aa',
    'var(--color-text-muted)': '#3d5269',
  };
  ctx.fillStyle = ratingColorMap[stats.ratingColor] ?? '#38bdf8';
  ctx.font = '700 28px monospace';
  ctx.fillText(stats.rating.toUpperCase(), 40, 85);

  // WPM
  ctx.fillStyle = '#818cf8';
  ctx.font = '700 64px monospace';
  ctx.fillText(String(stats.wpm), 40, 175);
  ctx.fillStyle = '#3d5269';
  ctx.font = '600 11px monospace';
  ctx.fillText('WPM', 42, 195);
  ctx.fillText(`raw ${stats.rawWpm}`, 42, 210);

  // Accuracy
  ctx.fillStyle = '#34d399';
  ctx.font = '700 40px monospace';
  ctx.fillText(`${stats.accuracy}%`, 200, 165);
  ctx.fillStyle = '#3d5269';
  ctx.font = '11px monospace';
  ctx.fillText('ACCURACY', 200, 182);

  // Time + difficulty
  ctx.fillStyle = '#f472b6';
  ctx.font = '700 32px monospace';
  ctx.fillText(`${stats.timeMode}s`, 360, 165);
  ctx.fillStyle = '#3d5269';
  ctx.font = '11px monospace';
  ctx.fillText('TIME', 360, 182);

  ctx.fillStyle = '#7a90aa';
  ctx.font = '700 24px monospace';
  ctx.fillText(stats.difficulty.toUpperCase(), 430, 165);
  ctx.fillStyle = '#3d5269';
  ctx.font = '11px monospace';
  ctx.fillText('MODE', 430, 182);

  // Separator
  ctx.strokeStyle = '#1e2a38';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(40, 225);
  ctx.lineTo(W - 40, 225);
  ctx.stroke();

  // Chars
  ctx.fillStyle = '#34d399';
  ctx.font = '11px monospace';
  ctx.fillText(`${stats.correct} correct`, 40, 248);
  ctx.fillStyle = '#f87171';
  ctx.fillText(`${stats.incorrect} incorrect`, 160, 248);
  if (stats.accuracy === 100) {
    ctx.fillStyle = '#38bdf8';
    ctx.fillText('PERFECT ACCURACY', 40, 270);
  }

  // Bottom brand
  ctx.fillStyle = '#1e2a38';
  ctx.fillRect(0, H - 28, W, 28);
  ctx.fillStyle = '#3d5269';
  ctx.font = '10px monospace';
  ctx.fillText('cryotype — precision typing test', 40, H - 10);

  canvas.toBlob(blob => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cryotype-${stats.wpm}wpm-${stats.accuracy}acc.png`;
    a.click();
    URL.revokeObjectURL(url);
  }, 'image/png');
}

/* ─── COMPONENT ─────────────────────────────────────────────────────────── */
interface ShareModalProps {
  stats: ShareStats;
  onClose: () => void;
}

export default function ShareModal({ stats, onClose }: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleCopyText = async () => {
    const text = [
      'CRYOTYPE — Typing Test Result',
      '─'.repeat(32),
      `WPM       : ${stats.wpm}`,
      `Raw WPM   : ${stats.rawWpm}`,
      `Accuracy  : ${stats.accuracy}%`,
      `Time      : ${stats.timeMode}s`,
      `Difficulty: ${stats.difficulty}`,
      `Rating    : ${stats.rating}`,
      `Chars     : ${stats.correct} correct · ${stats.incorrect} incorrect`,
      '─'.repeat(32),
    ].join('\n');

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: create a temp textarea
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <ModalBox>
        <ModalHeader>
          <ModalTitle>Share Result</ModalTitle>
          <CloseBtn onClick={onClose} aria-label="Close">&#x2715;</CloseBtn>
        </ModalHeader>

        <ShareCard ref={cardRef}>
          <CardBrand>cryotype &nbsp;·&nbsp; precision typing test</CardBrand>
          <CardRating $color={stats.ratingColor}>{stats.rating}</CardRating>
          <CardStatsRow>
            <CardStat>
              <CardStatValue $color="var(--color-wpm)">{stats.wpm}</CardStatValue>
              <CardStatLabel>wpm</CardStatLabel>
              <CardRawLine>raw {stats.rawWpm}</CardRawLine>
            </CardStat>
            <CardStat>
              <CardStatValue $color="var(--color-accuracy)">{stats.accuracy}%</CardStatValue>
              <CardStatLabel>accuracy</CardStatLabel>
            </CardStat>
            <CardStat>
              <CardStatValue $color="var(--color-time)">{stats.timeMode}s</CardStatValue>
              <CardStatLabel>time</CardStatLabel>
            </CardStat>
          </CardStatsRow>
          <CardMeta>
            {stats.difficulty} mode &nbsp;·&nbsp; {stats.correct} correct &nbsp;·&nbsp; {stats.incorrect} incorrect
          </CardMeta>
        </ShareCard>

        <Actions>
          {copied ? (
            <Feedback>Copied!</Feedback>
          ) : (
            <ActionBtn onClick={handleCopyText}>Copy Text</ActionBtn>
          )}
          <ActionBtn $primary onClick={() => downloadPng(stats)}>
            Download PNG
          </ActionBtn>
        </Actions>
      </ModalBox>
    </Overlay>
  );
}
